import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { GateGroup } from '../../../../models/scan-testing.model';

interface DraftGroup {
  id: string;
  name: string;
  gateNames: string[];
  nameError: boolean;
}

export interface CopyTarget {
  timestamp: number;
  description: string;
  date: string;
  hasGroups?: boolean;
  status?: 'pending' | 'saving' | 'done' | 'error';
  error?: string;
}

export interface CopyTargetsRequest {
  timestamps: number[];
  groupIds: string[];
}

@Component({
  selector: 'app-gate-groups',
  templateUrl: './gate-groups.component.html',
  standalone: false,
  styleUrls: ['./gate-groups.component.scss']
})
export class GateGroupsComponent implements OnChanges {
  @Input() gateNames: string[] = [];
  @Input() existingGroups: GateGroup[] = [];
  @Input() saving = false;
  @Input() error = '';

  @Input() copyTargets: CopyTarget[] = [];
  @Input() copyLoading = false;
  @Input() copyRunning = false;
  @Input() copyError = '';
  @Input() copyDone = false;

  @Output() save = new EventEmitter<GateGroup[]>();
  @Output() cancel = new EventEmitter<void>();
  @Output() copy = new EventEmitter<CopyTargetsRequest>();
  @Output() openCopy = new EventEmitter<void>();
  @Output() closeCopy = new EventEmitter<void>();

  view: 'edit' | 'copy' = 'edit';
  selectedTargets = new Set<number>();
  selectedGroupIds = new Set<string>();

  groups: DraftGroup[] = [];
  ungrouped: string[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gateNames'] || changes['existingGroups']) {
      this.rebuildFromInputs();
    }
  }

  private rebuildFromInputs() {
    const used = new Set<string>();
    this.groups = (this.existingGroups || []).map((g) => {
      const filtered = (g.gateNames || []).filter((n) => this.gateNames.includes(n));
      filtered.forEach((n) => used.add(n));
      return {
        id: g.id,
        name: g.name,
        gateNames: filtered,
        nameError: false
      };
    });
    this.ungrouped = this.gateNames.filter((n) => !used.has(n));
  }

  trackGroup(_idx: number, g: DraftGroup) { return g.id; }
  trackGate(_idx: number, n: string) { return n; }

  groupListIds(): string[] {
    return this.groups.map((g) => g.id);
  }

  addGroup() {
    const id = 'g_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    this.groups = [...this.groups, { id, name: '', gateNames: [], nameError: false }];
  }

  removeGroup(group: DraftGroup) {
    const restored = [...this.ungrouped, ...group.gateNames];
    this.ungrouped = this.gateNames.filter((n) => restored.includes(n));
    this.groups = this.groups.filter((g) => g.id !== group.id);
  }

  onDrop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
  }

  validate(): boolean {
    let ok = true;
    const seen = new Set<string>();
    for (const g of this.groups) {
      const trimmed = g.name.trim();
      const duplicate = !!trimmed && seen.has(trimmed.toLowerCase());
      if (!trimmed || duplicate) {
        g.nameError = true;
        ok = false;
      } else {
        g.nameError = false;
        seen.add(trimmed.toLowerCase());
      }
    }
    return ok;
  }

  onSave() {
    if (this.saving) { return; }
    if (!this.validate()) { return; }
    const out: GateGroup[] = this.groups
      .filter((g) => g.gateNames.length > 0)
      .map((g) => ({ id: g.id, name: g.name.trim(), gateNames: [...g.gateNames] }));
    this.save.emit(out);
  }

  onCancel() {
    if (this.saving || this.copyRunning) { return; }
    this.cancel.emit();
  }

  trackTarget(_idx: number, t: CopyTarget) { return t.timestamp; }

  enterCopyView() {
    if (this.saving || this.copyRunning) { return; }
    if (!this.groups.length) { return; }
    this.selectedTargets.clear();
    this.selectedGroupIds = new Set(
      this.groups.filter((g) => g.gateNames.length > 0).map((g) => g.id)
    );
    this.view = 'copy';
    this.openCopy.emit();
  }

  exitCopyView() {
    if (this.copyRunning) { return; }
    this.view = 'edit';
    this.selectedTargets.clear();
    this.selectedGroupIds.clear();
    this.closeCopy.emit();
  }

  copyableGroups(): DraftGroup[] {
    return this.groups.filter((g) => g.gateNames.length > 0);
  }

  isGroupSelectedForCopy(g: DraftGroup): boolean {
    return this.selectedGroupIds.has(g.id);
  }

  toggleGroupForCopy(g: DraftGroup) {
    if (this.copyRunning) { return; }
    if (this.selectedGroupIds.has(g.id)) {
      this.selectedGroupIds.delete(g.id);
    } else {
      this.selectedGroupIds.add(g.id);
    }
  }

  toggleAllGroupsForCopy() {
    if (this.copyRunning) { return; }
    const all = this.copyableGroups();
    if (this.selectedGroupIds.size === all.length) {
      this.selectedGroupIds.clear();
    } else {
      this.selectedGroupIds = new Set(all.map((g) => g.id));
    }
  }

  get allGroupsSelectedForCopy(): boolean {
    const all = this.copyableGroups();
    return all.length > 0 && this.selectedGroupIds.size === all.length;
  }

  isTargetSelected(t: CopyTarget): boolean {
    return this.selectedTargets.has(t.timestamp);
  }

  toggleTarget(t: CopyTarget) {
    if (this.copyRunning) { return; }
    if (this.selectedTargets.has(t.timestamp)) {
      this.selectedTargets.delete(t.timestamp);
    } else {
      this.selectedTargets.add(t.timestamp);
    }
  }

  toggleAllTargets() {
    if (this.copyRunning) { return; }
    if (this.selectedTargets.size === this.copyTargets.length) {
      this.selectedTargets.clear();
    } else {
      this.selectedTargets = new Set(this.copyTargets.map((t) => t.timestamp));
    }
  }

  get allTargetsSelected(): boolean {
    return this.copyTargets.length > 0 && this.selectedTargets.size === this.copyTargets.length;
  }

  get hasGroupsToCopy(): boolean {
    return this.groups.some((g) => g.gateNames.length > 0);
  }

  onCopyConfirm() {
    if (this.copyRunning || this.copyLoading) { return; }
    if (!this.selectedTargets.size || !this.selectedGroupIds.size) { return; }
    this.copy.emit({
      timestamps: Array.from(this.selectedTargets),
      groupIds: Array.from(this.selectedGroupIds)
    });
  }
}
